import { printHtmlWithQz, printPdfWithQz } from './qzPrintService';
import { PRINT_QUEUE_ITEM_STATES } from './printQueueModel';
import {
  inferQueuedPayloadType,
  resolveQzSettleDelayMs,
} from '../utils/qzHtmlSpoolGroups';

const wait = (milliseconds) =>
  new Promise((resolve) => setTimeout(resolve, Math.max(0, milliseconds)));

class QzPrintExecutor {
  constructor() {
    this.runPromise = null;
    this.heldPayload = null;
    this.activeItemId = null;
  }

  isRunning() {
    return Boolean(this.runPromise);
  }

  async start(controller) {
    if (this.runPromise) return this.runPromise;
    this.runPromise = this.execute(controller).finally(() => {
      this.runPromise = null;
      this.activeItemId = null;
    });
    return this.runPromise;
  }

  async execute(controller) {
    await controller.onRunState({ running: true, pauseRequested: false });
    while (true) {
      const snapshot = controller.getSnapshot();
      if (snapshot.pauseRequested || snapshot.blockedItemId) break;
      const item = controller.getNextItem(snapshot);
      if (!item) break;
      this.activeItemId = item.id;
      await controller.onItemState(item.id, {
        status: PRINT_QUEUE_ITEM_STATES.VALIDATING,
        error: '',
      });
      let payload = null;
      try {
        await controller.preflight(item);
        await controller.onItemState(item.id, {
          status: PRINT_QUEUE_ITEM_STATES.GENERATING,
        });
        payload = await controller.materialize(item);
        this.heldPayload = payload;
        await controller.onItemState(item.id, {
          status: PRINT_QUEUE_ITEM_STATES.SUBMITTING,
          attempts: (Number(item.attempts) || 0) + 1,
          byteLength: payload.byteLength || 0,
        });
        const settings = item.routing || {};
        if (payload.payloadType === 'skip') {
          await controller.onItemState(item.id, {
            status: PRINT_QUEUE_ITEM_STATES.SKIPPED,
            error: payload.skipReason || 'Skipped in mixed queue.',
          });
          payload.release?.();
          payload = null;
          this.heldPayload = null;
          continue;
        }
        if (payload.payloadType === 'pdf') {
          await printPdfWithQz(
            item.profileKey,
            payload.pdfBase64,
            settings,
            {
              copies: item.recipe?.copies,
              pageRanges: payload.pageRanges || item.recipe?.pageRanges || '',
              pageCount: payload.pageCount,
            },
          );
        } else {
          await printHtmlWithQz(item.profileKey, payload.html, {
            ...settings,
            jobName: `London Smoke & Cure - ${item.id}`,
          });
        }
        await controller.onItemState(item.id, {
          status: PRINT_QUEUE_ITEM_STATES.ACCEPTED,
          acceptedAt: new Date().toISOString(),
          physicalPages: payload.physicalPages || payload.pageCount || 1,
        });
        const previousPayloadType = payload.payloadType;
        payload.release?.();
        payload = null;
        this.heldPayload = null;
        const nextSnapshot = controller.getSnapshot();
        const nextItem =
          nextSnapshot.pauseRequested || nextSnapshot.blockedItemId
            ? null
            : controller.getNextItem(nextSnapshot);
        const settleMs = resolveQzSettleDelayMs({
          configuredMs: nextSnapshot.settleDelayMs,
          hasNext: Boolean(nextItem),
          previousPrinter: item.routing?.printer,
          nextPrinter: nextItem?.routing?.printer,
          previousTray: item.routing?.printerTray,
          nextTray: nextItem?.routing?.printerTray,
          previousPayloadType,
          nextPayloadType: nextItem ? inferQueuedPayloadType(nextItem) : '',
        });
        if (settleMs > 0) {
          await controller.onItemState(item.id, {
            status: PRINT_QUEUE_ITEM_STATES.SETTLING,
          });
          await wait(settleMs);
        }
        await controller.onItemState(item.id, {
          status: PRINT_QUEUE_ITEM_STATES.AWAITING_CONFIRMATION,
        });
      } catch (error) {
        const uncertain = error?.submissionStarted === true;
        if (!uncertain) {
          payload?.release?.();
          payload = null;
          this.heldPayload = null;
        }
        await controller.onItemState(item.id, {
          status: uncertain
            ? PRINT_QUEUE_ITEM_STATES.UNCERTAIN
            : PRINT_QUEUE_ITEM_STATES.PAUSED,
          error: error?.message || 'Queued printing failed.',
          requiresAcknowledgement: error?.warningOnly === true,
        });
        await controller.onRunState({
          running: false,
          blockedItemId: uncertain ? item.id : null,
          pauseRequested: true,
        });
        return;
      }
    }
    const finalSnapshot = controller.getSnapshot();
    await controller.onRunState({
      running: false,
      pauseRequested: Boolean(finalSnapshot.pauseRequested),
    });
  }

  releaseHeldPayload() {
    this.heldPayload?.release?.();
    this.heldPayload = null;
  }
}

export const qzPrintExecutor = new QzPrintExecutor();
export default qzPrintExecutor;
